import { createInstance } from '@optimizely/optimizely-sdk';
import { isBrowser } from '../ssr';
import { checkOptanonConsentGroup, getLogLevel } from './shared';
var OPTIMIZELY_CONSENT_GROUP = 'C0002:1';
// Optimizely client instance
var optimizelyClient = null;
/**
 * Initializes the Optimizely client instance.
 *
 * @param logLevel - The log level to use for the Optimizely client.
 * @returns An `OptimizelyClient` instance.
 */
var initializeOptimizelyClient = function (logLevel) {
    var instance = createInstance({
        sdkKey: process.env.NEXT_PUBLIC_OPTIMIZELY_FULL_STACK,
        logLevel: logLevel
    });
    if (!instance) {
        throw new Error('Failed to initialize Optimizely client.');
    }
    // Attach client to `window` for GTM access
    if (isBrowser() && !window.optimizelyClient) {
        Object.defineProperty(window, 'optimizelyClient', {
            value: instance,
            writable: false,
            configurable: false
        });
    }
    return instance;
};
/**
 * Retrieves an instance of the Optimizely client for browser use.
 *
 * Ensures that the Optimizely client is only initialized on the browser
 * and not on the server. If the client has already been initialized,
 * it returns the existing instance.
 *
 * @returns An `OptimizelyClient` instance if successful,
 * or `null` if the initialization fails or if executed on the server-side.
 */
export var getOptimizelyClient = function () {
    if (!isBrowser())
        return null;
    var bomCountry = localStorage.getItem('_bomCountry');
    var isOptimizelyAllowed = bomCountry === 'US';
    // Ensure Optimizely is only used in the US
    if (!isOptimizelyAllowed) {
        // eslint-disable-next-line
        console.error("[BOM-COUNTRY]: Missing or invalid bomCountry ('".concat(bomCountry, "')"));
        return null;
    }
    if (!optimizelyClient) {
        var logLevel = getLogLevel();
        optimizelyClient = initializeOptimizelyClient(logLevel);
    }
    return optimizelyClient;
};
/**
 * Retrieves an instance of the Optimizely client for server-side use.
 *
 * Ensures that the Optimizely client is only initialized once on the server.
 */
export var getOptimizelyClientSSR = function (req, logLevel) {
    if (logLevel === void 0) { logLevel = 'error'; }
    var cookies = req.cookies;
    var optanonCookie = cookies.OptanonConsent;
    var isOptimizelyAllowed = checkOptanonConsentGroup(optanonCookie, OPTIMIZELY_CONSENT_GROUP);
    if (!isOptimizelyAllowed) {
        return null;
    }
    if (!optimizelyClient) {
        optimizelyClient = initializeOptimizelyClient(logLevel);
    }
    return optimizelyClient;
};
